import React, { useEffect } from "react";

import moment from "moment";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import DailyTemplateToApply from "./DailyTemplateToApply";
import { TIME_FORMAT_SHORT } from "../../../constants/dateFormat";
import useFast from "../../../contexts/FASTContext";
import { formatTemplateOption, getWeekDays } from "../../../utils/fast";
import DateRangePicker from "../../form/DateRangePicker";
import TimePicker from "../../form/TimePicker";

import type { FormEvent, JSX } from "react";

import type { FormValues } from "./CalendarSchedulingModal";
import type { Template } from "../../../api/FastAPI.types";

interface DailyTemplateToApplyProps {
  formId: string;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  templatesList: Template[];
  isLoading: boolean;
}

function CalendarSchedulingModalForm({
  formId,
  onSubmit,
  templatesList,
  isLoading,
}: DailyTemplateToApplyProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const { state } = useFast();
  const { watch, setValue } = useFormContext<FormValues>();

  const days = getWeekDays(state.channelData?.first_day_of_week);

  const startDate = watch("templateRange.startDate");
  const startTime = watch("templateRange.startTime");

  const isStartDateToday = startDate
    ? moment(startDate).isSame(moment(), "day")
    : false;

  // The start time must be at least 5 minutes after the current time.
  const minStartTime = moment().add(5, "minutes").format(TIME_FORMAT_SHORT);

  // The start time is only relevant for the current day. Default it to the
  // earliest allowed time when the range starts today, and clear it otherwise
  // so it is not sent to the API.
  useEffect(() => {
    if (isStartDateToday && !startTime) {
      setValue("templateRange.startTime", minStartTime);
    } else if (!isStartDateToday && startTime) {
      setValue("templateRange.startTime", undefined);
    }
  }, [isStartDateToday, startTime, minStartTime, setValue]);

  const validateStartTime = (value?: string): string | boolean => {
    if (!isStartDateToday || !value) return true;

    return (
      moment(value, TIME_FORMAT_SHORT).isSameOrAfter(
        moment().add(5, "minutes"),
        "minute",
      ) || t("startTimeMinError")
    );
  };

  const templatesOptions =
    templatesList.map((template) => formatTemplateOption(template)) || [];

  const minDate = moment(new Date()).toDate();

  return (
    <form
      className="flex flex-col gap-[1.875rem]"
      id={formId}
      onSubmit={onSubmit}
    >
      <div className="flex flex-col gap-[0.3125rem]">
        <div className="font-semibold">
          {t("selectTimerangeToApplyTemplate")}
        </div>
        <DateRangePicker
          isCompact
          className="mb-0! flex gap-[0.9375rem]"
          endLabel={t("to")}
          minDate={minDate}
          name="templateRange"
          startLabel={t("from")}
        />
        <TimePicker
          isCompact
          className="mb-0! w-[calc(50%-0.46875rem)]!"
          isDisabled={!isStartDateToday}
          label={t("startTime")}
          min={isStartDateToday ? minStartTime : undefined}
          name="templateRange.startTime"
          tooltip={t("startTimeTooltip")}
          validate={validateStartTime}
        />
      </div>
      <div className="flex flex-col gap-[0.46875rem]">
        <div className="font-semibold">{t("selectDailyTemplateToApply")}</div>
        <div className="flex flex-col gap-[0.46875rem]">
          {days.map((day) => (
            <DailyTemplateToApply
              key={day}
              day={day}
              isLoading={isLoading}
              templatesOptions={templatesOptions}
            />
          ))}
        </div>
      </div>
    </form>
  );
}

export default CalendarSchedulingModalForm;
